import React, { useState, useEffect, useCallback, useRef } from "react";
import "./css/MainCommon.css";
import "./css/LandingPage-Usage.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faShieldAlt,
  faChevronLeft,
  faChevronRight,
  faCircle,
  faSms,
  faEnvelope,
  faLaptopCode,
  faShareAlt,
} from "@fortawesome/free-solid-svg-icons";

const AUTOPLAY_INTERVAL = 6000; // 6 seconds per slide

const slides = [
  {
    image: "/TextAnalysis.png",
    alt: "KinShield analyzing a suspicious text message for scam indicators",
    title: "Text Message Analysis",
    description:
      "Paste or forward any suspicious text message and get an instant AI-powered scam assessment with detailed risk indicators.",
    icon: faSms,
  },
  {
    image: "/EmailAnalysis.png",
    alt: "KinShield analyzing a suspicious email for phishing and fraud",
    title: "Email Analysis",
    description:
      "Forward suspicious emails to KinShield for comprehensive phishing detection, sender verification, and threat analysis.",
    icon: faEnvelope,
  },
  {
    image: "/TechSupportScamAnalysis.png",
    alt: "KinShield detecting a tech support scam attempt",
    title: "Tech Support Scam Detection",
    description:
      "Screenshot pop-ups, alerts, or suspicious websites and let KinShield identify tech support scams before you fall victim.",
    icon: faLaptopCode,
  },
  {
    image: "/SocialMediaAnalysis.png",
    alt: "KinShield analyzing suspicious social media content for scams",
    title: "Social Media Scam Detection",
    description:
      "Analyze suspicious social media posts, ads, and messages across platforms to identify fraud, fake offers, and impersonation scams.",
    icon: faShareAlt,
  },
];

function LandingPageUsage() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [direction, setDirection] = useState("next");
  const [isTransitioning, setIsTransitioning] = useState(false);
  const autoPlayRef = useRef(null);
  const touchStartX = useRef(null);
  const touchEndX = useRef(null);

  const goToSlide = useCallback(
    (index, dir = "next") => {
      if (isTransitioning) return;
      setIsTransitioning(true);
      setDirection(dir);
      setCurrentSlide(index);
      setTimeout(() => setIsTransitioning(false), 700);
    },
    [isTransitioning],
  );

  const nextSlide = useCallback(() => {
    const next = (currentSlide + 1) % slides.length;
    goToSlide(next, "next");
  }, [currentSlide, goToSlide]);

  const prevSlide = useCallback(() => {
    const prev = (currentSlide - 1 + slides.length) % slides.length;
    goToSlide(prev, "prev");
  }, [currentSlide, goToSlide]);

  // Autoplay
  useEffect(() => {
    if (isAutoPlaying) {
      autoPlayRef.current = setInterval(nextSlide, AUTOPLAY_INTERVAL);
    }
    return () => {
      if (autoPlayRef.current) clearInterval(autoPlayRef.current);
    };
  }, [isAutoPlaying, nextSlide]);

  // Pause autoplay on hover, resume on leave
  const handleMouseEnter = () => setIsAutoPlaying(false);
  const handleMouseLeave = () => setIsAutoPlaying(true);

  // Touch/swipe support
  const handleTouchStart = (e) => {
    touchStartX.current = e.changedTouches[0].screenX;
  };

  const handleTouchEnd = (e) => {
    touchEndX.current = e.changedTouches[0].screenX;
    const diff = touchStartX.current - touchEndX.current;
    if (Math.abs(diff) > 50) {
      if (diff > 0) nextSlide();
      else prevSlide();
    }
  };

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "ArrowLeft") prevSlide();
      if (e.key === "ArrowRight") nextSlide();
    },
    [prevSlide, nextSlide],
  );

  const scrollToTop = () => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    document.documentElement.scrollTop = 0;
  };

  return (
    <section className="usage-section" aria-label="KinShield in Action">
      <div className="scroll-to-top-link" onClick={scrollToTop}>
        Top ↑
      </div>

      {/* Section Header */}
      <div className="usage-header">
        <h2 className="usage-title">
          <FontAwesomeIcon icon={faShieldAlt} className="usage-title-icon" />
          See KinShield™ in Action
        </h2>
        <p className="usage-subtitle">
          Real examples of how KinShield protects you from scams across text
          messages, emails, social media, and tech support fraud.
        </p>
      </div>

      {/* Carousel */}
      <div
        className="usage-carousel"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        role="region"
        aria-roledescription="carousel"
        aria-label="KinShield usage examples"
      >
        {/* Previous Arrow */}
        <button
          className="carousel-arrow carousel-arrow-left"
          onClick={prevSlide}
          aria-label="Previous slide"
        >
          <FontAwesomeIcon icon={faChevronLeft} />
        </button>

        {/* Slide Container */}
        <div className="carousel-viewport">
          {slides.map((slide, index) => (
            <div
              key={index}
              className={`carousel-slide ${
                index === currentSlide ? "active" : ""
              } ${
                index === currentSlide
                  ? `slide-enter-${direction}`
                  : "slide-hidden"
              }`}
              role="tabpanel"
              aria-roledescription="slide"
              aria-label={`Slide ${index + 1} of ${slides.length}: ${slide.title}`}
              aria-hidden={index !== currentSlide}
            >
              <div className="slide-content">
                <div className="slide-image-container">
                  <img
                    src={slide.image}
                    alt={slide.alt}
                    className="slide-image"
                    loading={index === 0 ? "eager" : "lazy"}
                    draggable="false"
                  />
                </div>
                <div className="slide-info">
                  <div className="slide-info-icon">
                    <FontAwesomeIcon icon={slide.icon} />
                  </div>
                  <h3 className="slide-title">{slide.title}</h3>
                  <p className="slide-description">{slide.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Next Arrow */}
        <button
          className="carousel-arrow carousel-arrow-right"
          onClick={nextSlide}
          aria-label="Next slide"
        >
          <FontAwesomeIcon icon={faChevronRight} />
        </button>

        {/* Dot Indicators */}
        <div
          className="carousel-dots"
          role="tablist"
          aria-label="Slide indicators"
        >
          {slides.map((slide, index) => (
            <button
              key={index}
              className={`carousel-dot ${index === currentSlide ? "active" : ""}`}
              onClick={() =>
                goToSlide(index, index > currentSlide ? "next" : "prev")
              }
              role="tab"
              aria-selected={index === currentSlide}
              aria-label={`Go to slide ${index + 1}: ${slide.title}`}
            >
              <FontAwesomeIcon icon={faCircle} />
            </button>
          ))}
        </div>

        {/* Progress Bar */}
        <div className="carousel-progress">
          <div
            className="carousel-progress-bar"
            style={{
              animationDuration: `${AUTOPLAY_INTERVAL}ms`,
              animationPlayState: isAutoPlaying ? "running" : "paused",
            }}
            key={currentSlide}
          />
        </div>
      </div>
    </section>
  );
}

export default LandingPageUsage;
